import { Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { ChevronDown, Menu, X, LogOut, User as UserIcon, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { useSubscription } from "@/hooks/use-subscription";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";

type UseCaseLink = { icon: string; name: string; href: string };
type UseCaseColumn = { title: string; links: UseCaseLink[] };

const useCaseColumns: UseCaseColumn[] = [
  {
    title: "🏠 Renters & Tenants",
    links: [
      { icon: "🏠", name: "Lease Agreement Review", href: "/lease-decoder" },
      { icon: "🔑", name: "Rental Application Check", href: "/" },
      { icon: "📋", name: "Move-in Inspection Checklist", href: "/" },
    ],
  },
  {
    title: "💼 Freelancers",
    links: [
      { icon: "📝", name: "NDA Review", href: "/nda-scanner" },
      { icon: "💼", name: "Service Contract Analysis", href: "/freelancer-contracts" },
      { icon: "📄", name: "Independent Contractor Agreement", href: "/freelancer-contracts" },
    ],
  },
  {
    title: "🚀 Entrepreneurs",
    links: [
      { icon: "💰", name: "Investment Term Sheet Review", href: "/" },
      { icon: "🤝", name: "Partnership Agreement", href: "/" },
      { icon: "📑", name: "Vendor Contract", href: "/" },
    ],
  },
  {
    title: "👥 HR & Employers",
    links: [
      { icon: "👥", name: "Employment Contract", href: "/" },
      { icon: "⚖️", name: "Severance Agreement", href: "/" },
      { icon: "🔒", name: "Non-Compete Review", href: "/non-compete" },
    ],
  },
];

export function Header() {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const [useCasesOpen, setUseCasesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileUseCasesOpen, setMobileUseCasesOpen] = useState(false);
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { isPro } = useSubscription();
  const isCapacitor = isMounted && typeof window !== "undefined" && !!(window as any).Capacitor;

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast.success("Signed out");
    navigate({ to: "/" });
  };

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo />

          <nav className="hidden items-center gap-8 md:flex">
            {isCapacitor ? (
              <>
                <Link
                  to="/"
                  className="text-sm font-medium text-[#374151] transition-colors hover:text-[#1B3A6B]"
                >
                  Analyze
                </Link>
                {user && (
                  <Link
                    to="/account"
                    className="text-sm font-medium text-[#374151] transition-colors hover:text-[#1B3A6B]"
                  >
                    Account/Saved Analyses
                  </Link>
                )}
              </>
            ) : (
              <>
                {/* Use Cases dropdown */}
                <div
                  className="relative"
                  onMouseEnter={() => setUseCasesOpen(true)}
                  onMouseLeave={() => setUseCasesOpen(false)}
                >
                  <button className="flex items-center gap-1 text-sm font-medium text-[#374151] transition-colors hover:text-[#1B3A6B]">
                    Use Cases
                    <ChevronDown
                      className={cn("h-4 w-4 transition-transform", useCasesOpen && "rotate-180")}
                    />
                  </button>
                  {useCasesOpen && (
                    <div className="absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3">
                      <div
                        className="grid w-[820px] grid-cols-4 gap-8 rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-lg"
                        style={{ animation: "fadeInUp 0.2s ease-out" }}
                      >
                        {useCaseColumns.map((col) => (
                          <div key={col.title}>
                            <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.05em] text-[#9CA3AF]">
                              {col.title}
                            </div>
                            <ul className="space-y-2">
                              {col.links.map((link) => (
                                <li key={link.name}>
                                  <Link
                                    to={link.href as any}
                                    className="flex items-start gap-2 rounded-md px-2 py-1.5 text-[14px] text-[#374151] transition-colors hover:bg-[#EEF2FF] hover:text-[#1B3A6B]"
                                    onClick={() => setUseCasesOpen(false)}
                                  >
                                    <span>{link.icon}</span>
                                    <span>{link.name}</span>
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <Link
                  to="/how-it-works"
                  className="text-sm font-medium text-[#374151] transition-colors hover:text-[#1B3A6B]"
                >
                  Document Types
                </Link>
                <Link
                  to="/blog"
                  className="text-sm font-medium text-[#374151] transition-colors hover:text-[#1B3A6B]"
                >
                  Blog
                </Link>
                <Link
                  to="/pricing"
                  className="text-sm font-medium text-[#374151] transition-colors hover:text-[#1B3A6B]"
                >
                  Pricing
                </Link>
                {user && (
                  <Link
                    to="/account"
                    className="text-sm font-medium text-[#374151] transition-colors hover:text-[#1B3A6B]"
                  >
                    Dashboard
                  </Link>
                )}
                {user && (
                  <Link
                    to="/clause-history"
                    className="text-sm font-medium text-[#374151] transition-colors hover:text-[#1B3A6B]"
                  >
                    Clause History
                  </Link>
                )}
              </>
            )}
          </nav>

          <div className="hidden items-center gap-3 md:flex">
            <ThemeToggle />
            {!loading && user ? (
              <>
                {isPro && !isCapacitor && (
                  <span className="hidden items-center gap-1 rounded-full bg-primary-glow/10 px-2 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-glow lg:flex">
                    <Crown className="h-3 w-3" /> Pro
                  </span>
                )}
                <Link
                  to="/account"
                  className="hidden items-center gap-1.5 text-sm text-[#6B7280] hover:text-foreground lg:flex"
                  title={user.email ?? ""}
                >
                  <UserIcon className="h-4 w-4" />
                  <span className="max-w-[140px] truncate">{user.email}</span>
                </Link>
                <Button variant="ghost" size="sm" onClick={handleSignOut} aria-label="Sign out">
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <Link
                to="/auth"
                className="text-sm font-medium text-[#6B7280] transition-colors hover:text-[#1B3A6B]"
              >
                Log in
              </Link>
            )}
            {!isCapacitor && (
              <Button variant="hero" className="rounded-xl px-5" asChild>
                <a href="/#analyzer">Try Free</a>
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <ThemeToggle />
            {!isCapacitor && (
              <Button variant="hero" size="sm" className="h-10 rounded-xl px-4 text-sm" asChild>
                <a href="/#analyzer">Try Free</a>
              </Button>
            )}
            <button
              className="flex h-11 w-11 items-center justify-center rounded-lg transition-colors hover:bg-muted/50 active:bg-muted"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav-panel"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile overlay - outside header to escape backdrop-blur containing block */}
      <div
        id="mobile-nav-panel"
        className={cn(
          "fixed inset-x-0 bottom-0 top-16 z-40 flex flex-col bg-background md:hidden",
          "transition-all duration-300 ease-out",
          mobileOpen
            ? "pointer-events-auto translate-y-0 opacity-100"
            : "pointer-events-none -translate-y-2 opacity-0",
        )}
        aria-hidden={!mobileOpen}
      >
        {isCapacitor ? (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex min-h-[48px] items-center border-b border-border py-3 text-base font-semibold text-foreground"
            >
              Analyze
            </Link>
            {user && (
              <Link
                to="/account"
                onClick={() => setMobileOpen(false)}
                className="flex min-h-[48px] items-center border-b border-border py-3 text-base font-semibold text-foreground"
              >
                Account/Saved Analyses
              </Link>
            )}
            {!loading &&
              (user ? (
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    handleSignOut();
                  }}
                  className="flex min-h-[48px] w-full items-center gap-2 border-b border-border py-3 text-base font-semibold text-[#ef4444] text-left hover:text-[#dc2626] transition-colors"
                >
                  Logout
                </button>
              ) : (
                <Link
                  to="/auth"
                  onClick={() => setMobileOpen(false)}
                  className="flex min-h-[48px] items-center gap-2 border-b border-border py-3 text-base font-semibold text-foreground"
                >
                  Log in
                </Link>
              ))}
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <button
                onClick={() => setMobileUseCasesOpen((v) => !v)}
                className="flex min-h-[48px] w-full items-center justify-between border-b border-border py-3 text-base font-semibold text-foreground"
                aria-expanded={mobileUseCasesOpen}
              >
                Use Cases
                <ChevronDown
                  className={cn(
                    "h-5 w-5 transition-transform duration-200",
                    mobileUseCasesOpen && "rotate-180",
                  )}
                />
              </button>
              <div
                className={cn(
                  "grid overflow-hidden transition-all duration-300 ease-out",
                  mobileUseCasesOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
                )}
              >
                <div className="min-h-0">
                  <div className="space-y-4 py-3">
                    {useCaseColumns.map((col) => (
                      <div key={col.title}>
                        <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          {col.title}
                        </div>
                        <ul className="space-y-1 pl-2">
                          {col.links.map((link) => (
                            <li key={link.name}>
                              <Link
                                to={link.href as any}
                                onClick={() => setMobileOpen(false)}
                                className="flex min-h-[44px] items-center gap-2 py-2 text-sm text-foreground/80"
                              >
                                <span>{link.icon}</span>
                                {link.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <Link
                to="/how-it-works"
                onClick={() => setMobileOpen(false)}
                className="flex min-h-[48px] items-center border-b border-border py-3 text-base font-semibold text-foreground"
              >
                Document Types
              </Link>
              <Link
                to="/blog"
                onClick={() => setMobileOpen(false)}
                className="flex min-h-[48px] items-center border-b border-border py-3 text-base font-semibold text-foreground"
              >
                Blog
              </Link>
              <Link
                to="/pricing"
                onClick={() => setMobileOpen(false)}
                className="flex min-h-[48px] items-center border-b border-border py-3 text-base font-semibold text-foreground"
              >
                Pricing
              </Link>
              {user && (
                <Link
                  to="/account"
                  onClick={() => setMobileOpen(false)}
                  className="flex min-h-[48px] items-center border-b border-border py-3 text-base font-semibold text-foreground"
                >
                  Dashboard
                </Link>
              )}
              {!loading &&
                (user ? (
                  <button
                    onClick={() => {
                      setMobileOpen(false);
                      handleSignOut();
                    }}
                    className="flex min-h-[48px] w-full items-center gap-2 py-3 text-sm font-medium"
                  >
                    <LogOut className="h-4 w-4" /> Sign out
                  </button>
                ) : (
                  <Link
                    to="/auth"
                    onClick={() => setMobileOpen(false)}
                    className="flex min-h-[48px] items-center gap-2 py-3 text-sm font-medium text-muted-foreground"
                  >
                    <UserIcon className="h-4 w-4" /> Log in
                  </Link>
                ))}
            </div>
            <div className="border-t border-border p-4">
              <Button variant="hero" className="h-12 w-full rounded-xl text-base" asChild>
                <a href="/#analyzer" onClick={() => setMobileOpen(false)}>
                  Try Free
                </a>
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
